import { useAccountant } from '../../contexts/AccountantContext';
import { useCompany } from '../../contexts/CompanyContext';
import { Badge } from '../ui/badge';
import { Buildings, CurrencyCircleDollar, Globe, Info } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

/**
 * ClientContextBanner - Shows which client company the accountant is currently working on
 * Use this at the top of forms and transaction pages to prevent confusion
 */
export function ClientContextBanner({ className, compact = false }) {
  const { isInClientContext, activeClient, accountantMode } = useAccountant();
  const { company, currency } = useCompany();

  // Don't show if not in accountant mode or not managing a client
  if (!accountantMode || !isInClientContext || !activeClient) {
    return null;
  }

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2 text-sm", className)}>
        <Badge variant="outline" className="bg-[#0f172a] text-white border-0 gap-1">
          <Buildings size={12} weight="fill" />
          {activeClient.company_name}
        </Badge>
        <Badge variant="outline" className="gap-1">
          <CurrencyCircleDollar size={12} />
          {currency}
        </Badge>
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-3 mb-4 flex items-center justify-between",
        className
      )}
      data-testid="client-context-banner"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#d4a84b]/20 flex items-center justify-center">
          <Buildings size={20} className="text-[#d4a84b]" weight="fill" />
        </div>
        <div>
          <p className="text-xs text-white/60 uppercase tracking-wider">Working on Client</p>
          <p className="font-semibold text-white" data-testid="context-company-name">
            {activeClient.company_name}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-white/80">
          <CurrencyCircleDollar size={16} />
          <span className="text-sm font-medium">{currency}</span>
        </div>
        {company?.country && (
          <div className="flex items-center gap-2 text-white/80">
            <Globe size={16} />
            <span className="text-sm">{getCountryName(company.country)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ClientContextIndicator - Small inline indicator for form headers
 */
export function ClientContextIndicator({ label = "Adding to" }) {
  const { isInClientContext, activeClient, accountantMode } = useAccountant();
  const { currency } = useCompany();

  if (!accountantMode || !isInClientContext || !activeClient) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
      <Info size={14} />
      <span>{label}:</span>
      <Badge variant="outline" className="bg-slate-100 dark:bg-slate-800 gap-1 text-xs">
        <Buildings size={10} />
        {activeClient.company_name}
      </Badge>
      <span className="text-xs">({currency})</span>
    </div>
  );
}

/**
 * withClientContext - HOC to wrap page components with context banner
 */
export function withClientContext(Component, options = {}) {
  return function WrappedComponent(props) {
    return (
      <>
        <ClientContextBanner compact={options.compact} />
        <Component {...props} />
      </>
    );
  };
}

// Helper function
function getCountryName(code) {
  const countries = {
    'AE': 'UAE',
    'SA': 'Saudi Arabia',
    'QA': 'Qatar',
    'KW': 'Kuwait',
    'BH': 'Bahrain',
    'OM': 'Oman',
    'IN': 'India',
    'GB': 'United Kingdom',
    'US': 'United States',
    'EU': 'European Union'
  };
  return countries[code] || code;
}

export default ClientContextBanner;
